import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import Badge from '../../components/ui/Badge';
import Alert from '../../components/ui/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import usePermissions from '../../hooks/usePermissions';

const API_URL = 'https://medigo-api.onrender.com/api';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    canDeleteUsers, 
    canToggleUserStatus,
    canVerifyUsers,
    canEditUserProfile,
    isSuperAdmin,
    isAdmin
  } = usePermissions();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    address: ''
  });

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        setError('Not authenticated. Please login again.');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/admin/users/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.success && response.data.data) {
        const userData = response.data.data;
        
        setUser(userData);
        setEditFormData({
          name: userData.name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          dateOfBirth: userData.profile?.dateOfBirth ? 
            new Date(userData.profile.dateOfBirth).toISOString().split('T')[0] : '',
          gender: userData.profile?.gender || '',
          address: userData.location?.address || ''
        });
      } else {
        const userData = response.data.data || response.data;
        if (userData && userData._id) {
          setUser(userData);
          setEditFormData({
            name: userData.name || '',
            email: userData.email || '',
            phone: userData.phone || '',
            dateOfBirth: userData.profile?.dateOfBirth ? 
              new Date(userData.profile.dateOfBirth).toISOString().split('T')[0] : '',
            gender: userData.profile?.gender || '',
            address: userData.location?.address || ''
          });
        } else {
          setError('User data not found in response');
        }
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!canToggleUserStatus) {
      setError('You do not have permission to change user status');
      return;
    }

    const isProtectedAdmin = user.role === 'admin' || user.role === 'superadmin';
    if (isProtectedAdmin) {
      setError('Cannot modify admin account status through this page');
      return;
    }

    try {
      const token = localStorage.getItem('medigo_token');
      
      const response = await axios.put(
        `${API_URL}/admin/users/${id}/status`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setSuccess(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
        fetchUserDetails();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to toggle user status');
    }
  };

  const handleVerifyUser = async () => {
    if (!canVerifyUsers) {
      setError('You do not have permission to verify users');
      return;
    }

    try {
      const token = localStorage.getItem('medigo_token');
      
      const response = await axios.put(
        `${API_URL}/admin/users/${id}/verify`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setSuccess('User verified successfully');
        fetchUserDetails();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify user');
    }
  };

  const handleUpdateUser = async () => {
  
  
  if (!canEditUserProfile) {
    setError('Only Super Admins can edit user profiles');
    setTimeout(() => setError(''), 3000);
    return;
  }

  const isProtectedAdmin = user.role === 'admin' || user.role === 'superadmin';
  if (isProtectedAdmin) {
    setError('Cannot edit admin accounts through this page');
    setTimeout(() => setError(''), 3000);
    return;
  }

  // Validate required fields
  if (!editFormData.name || !editFormData.email || !editFormData.phone) {
    setError('Name, email, and phone are required fields');
    setTimeout(() => setError(''), 3000);
    return;
  }

  try {
    setIsUpdating(true);
     
    const token = localStorage.getItem('medigo_token');
    if (!token) throw new Error('Authentication token not found');

    const response = await axios.put(
      `${API_URL}/admin/users/${id}`,
      editFormData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data && response.data.success) {
      setSuccess(response.data.message || 'User updated successfully');
      setError('');
      setShowEditModal(false);
      await fetchUserDetails();
      setTimeout(() => setSuccess(''), 3000);
    }
  } catch (err) {
    console.error('❌ Update error:', err);
    const errorMessage = err.response?.data?.message || 'Failed to update user';
    setError(errorMessage);
    setTimeout(() => setError(''), 5000);
  } finally {
    setIsUpdating(false);
  }
};

  
  const handleDeleteUser = async () => {
    if (!canDeleteUsers) {
      setError('Only Super Admins can delete users');
      setShowDeleteModal(false);
      return;
    }

    const isProtectedAdmin = user.role === 'admin' || user.role === 'superadmin';
    if (isProtectedAdmin) {
      setError('Cannot delete admin accounts through this page');
      setShowDeleteModal(false);
      return;
    }

    try {
      
      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setShowDeleteModal(false);
        return;
      }

      const response = await axios.delete(
        `${API_URL}/admin/users/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

     

      
      if (response.data && (response.data.success || response.status === 200)) {
        setSuccess(response.data.message || 'User deleted successfully');
        setError('');
        setShowDeleteModal(false);
        
        // Navigate back to users list after 2 seconds
        setTimeout(() => navigate('/admin/users'), 2000);
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      console.error('❌ Delete error:', err);
      console.error('Error response:', err.response);
      
      let errorMessage = 'Failed to delete user';
      
      if (err.response) {
        // Server responded with error
        errorMessage = err.response.data?.message || `Error: ${err.response.status} - ${err.response.statusText}`;
      } else if (err.request) {
        // Request made but no response
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        // Error in request setup
        errorMessage = err.message || 'Failed to delete user';
      }
      
      setError(errorMessage);
      setSuccess('');
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <Alert type="error" message="User not found" />
        <button
          onClick={() => navigate('/admin/users')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const isProtectedAdmin = user.role === 'admin' || user.role === 'superadmin';
  const canEdit = canEditUserProfile && !isProtectedAdmin;
  const canDelete = canDeleteUsers && !isProtectedAdmin;
  const canToggle = canToggleUserStatus && !isProtectedAdmin;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-2 hover:bg-gray-100 rounded"
            title="Back to Users"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900">User Details</h1>
              {isProtectedAdmin && <Shield className="w-7 h-7 text-purple-600" />}
            </div>
            <p className="text-gray-600 mt-1">View and manage user information</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit ? (
            <button
              onClick={() => setShowEditModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              title="Edit User Profile"
            >
              Edit User
            </button>
          ) : isProtectedAdmin ? (
            <button
              disabled
              className="px-4 py-2 bg-gray-300 text-gray-500 rounded cursor-not-allowed"
              title="Admin accounts cannot be edited here"
            >
              Edit User
            </button>
          ) : (
            <button
              disabled
              className="px-4 py-2 bg-gray-300 text-gray-500 rounded cursor-not-allowed"
              title="Only Super Admins can edit user profiles"
            >
              Edit User
            </button>
          )}
          
          {canDelete ? (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              title="Delete User"
            >
              Delete User
            </button>
          ) : isProtectedAdmin ? (
            <button
              disabled
              className="px-4 py-2 bg-gray-300 text-gray-500 rounded cursor-not-allowed"
              title="Admin accounts cannot be deleted here"
            >
              Delete User
            </button>
          ) : (
            <button
              disabled
              className="px-4 py-2 bg-gray-300 text-gray-500 rounded cursor-not-allowed"
              title="Only Super Admins can delete users"
            >
              Delete User
            </button>
          )}
        </div>
      </div>

      {/* Permission Warning */}
      {isProtectedAdmin && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-purple-900 font-medium">Protected Admin Account</p>
            <p className="text-purple-700 text-sm">
              This is an admin account. Admin accounts cannot be edited or deleted through the user management interface.
            </p>
          </div>
        </div>
      )}

      {!isSuperAdmin && isAdmin && !isProtectedAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-900 font-medium">Limited Admin Access</p>
            <p className="text-blue-700 text-sm">
              You can view all details and verify/activate users, but only Super Admins can edit profiles or delete accounts.
            </p>
          </div>
        </div>
      )}

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* User Info Card */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-blue-600 text-3xl font-bold">
              {user.name?.charAt(0).toUpperCase() || '?'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold">{user.name || 'No Name'}</h2>
                {isProtectedAdmin && <Shield className="w-6 h-6" />}
              </div>
              <p className="text-blue-100">{user.email || 'No Email'}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Status Badges */}
          <div className="flex gap-2 flex-wrap">
            <Badge variant={user.isActive ? 'success' : 'error'}>
              {user.isActive ? 'Active' : 'Inactive'}
            </Badge>
            <Badge variant={user.isVerified ? 'success' : 'warning'}>
              {user.isVerified ? 'Verified' : 'Unverified'}
            </Badge>
            <Badge variant={
              user.role === 'superadmin' ? 'error' :
              user.role === 'admin' ? 'error' :
              user.role === 'doctor' ? 'info' :
              user.role === 'hospital' ? 'success' :
              user.role === 'pharmacy' ? 'primary' :
              'default'
            }>
              {user.role === 'superadmin' ? 'Super Admin' : 
               user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Unknown'}
            </Badge>
          </div>

          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">Full Name</p>
                <p className="font-semibold">{user.name || 'N/A'}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold break-all">{user.email || 'N/A'}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-semibold">{user.phone || 'N/A'}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">Role</p>
                <p className="font-semibold capitalize">{user.role || 'N/A'}</p>
              </div>
              {user.profile?.gender && (
                <div className="p-4 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Gender</p>
                  <p className="font-semibold capitalize">{user.profile.gender}</p>
                </div>
              )}
              {user.profile?.dateOfBirth && (
                <div className="p-4 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Date of Birth</p>
                  <p className="font-semibold">
                    {new Date(user.profile.dateOfBirth).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">User ID</p>
                <p className="font-mono text-sm break-all">{user._id}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">Member Since</p>
                <p className="font-semibold">
                  {new Date(user.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Address Information */}
          {user.location?.address && (
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Address Information
              </h3>
              <div className="p-4 bg-gray-50 rounded">
                <p className="font-semibold">{user.location.address}</p>
              </div>
            </div>
          )}

          {/* Account Actions */}
          {!isProtectedAdmin && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Account Actions</h3>
              <div className="flex flex-wrap gap-3">
                {canToggle && (
                  <button
                    onClick={handleToggleStatus}
                    className={`px-4 py-2 rounded font-medium ${
                      user.isActive
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {user.isActive ? 'Deactivate Account' : 'Activate Account'}
                  </button>
                )}
                {!user.isVerified && canVerifyUsers && (
                  <button
                    onClick={handleVerifyUser}
                    className="px-4 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 font-medium"
                  >
                    Verify User
                  </button>
                )}
                {!canToggle && !isProtectedAdmin && (
                  <p className="text-sm text-gray-500 italic">
                    You don't have permission to modify this user's status
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Activity Stats */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Activity Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded text-center">
                <p className="text-2xl font-bold text-blue-600">0</p>
                <p className="text-sm text-gray-600">Consultations</p>
              </div>
              <div className="p-4 bg-green-50 rounded text-center">
                <p className="text-2xl font-bold text-green-600">0</p>
                <p className="text-sm text-gray-600">Orders</p>
              </div>
              <div className="p-4 bg-purple-50 rounded text-center">
                <p className="text-2xl font-bold text-purple-600">0</p>
                <p className="text-sm text-gray-600">Payments</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && canEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Edit User Profile</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone *</label>
                <input
                  type="tel"
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+254 XXX XXX XXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date of Birth</label>
                <input
                  type="date"
                  value={editFormData.dateOfBirth}
                  onChange={(e) => setEditFormData({ ...editFormData, dateOfBirth: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Gender</label>
                <select
                  value={editFormData.gender}
                  onChange={(e) => setEditFormData({ ...editFormData, gender: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <textarea
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Full address"
                />
              </div>
              <div className="flex gap-2 mt-6">
                <button
  onClick={handleUpdateUser}
  disabled={isUpdating}
  className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
>
  {isUpdating && (
    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  )}
  {isUpdating ? 'Saving...' : 'Save Changes'}
</button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
    <ConfirmDialog
  isOpen={showDeleteModal} 
  onClose={() => setShowDeleteModal(false)} 
  onConfirm={handleDeleteUser}
  title="Delete User"
  message={`Are you sure you want to delete ${user?.name || 'this user'}? This action cannot be undone.`}
  confirmText="Delete User"
  cancelText="Cancel"
  type="danger"
/>
    </div>
  );
};

export default UserDetails;